import { isValid, startOfDay } from "date-fns";
import { MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID, normalizeMultiDimensionType } from "../workflowVariables.constants";

/**
 * @param {*} value
 * @param {"text"|"number"|"date"} type
 */
export function hasActiveFilterValue(value, type) {
  const t = normalizeMultiDimensionType(type);
  if (t === "date") {
    return value != null && isValid(value);
  }
  if (t === "number") {
    return value !== null && value !== undefined && !Number.isNaN(Number(value));
  }
  return String(value ?? "").trim() !== "";
}

/**
 * @param {string} raw
 */
function parseCellNumber(raw) {
  if (raw === "" || raw == null) return NaN;
  const n = parseFloat(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

/**
 * @param {string} raw
 * @returns {Date|null}
 */
function parseCellDate(raw) {
  if (raw === "" || raw == null) return null;
  const d = new Date(raw);
  return isValid(d) ? startOfDay(d) : null;
}

/**
 * @param {Date|null} d
 */
function startDayOrNull(d) {
  if (!d || !isValid(d)) return null;
  return startOfDay(d);
}

/**
 * @param {string} rawCell
 * @param {string} operator
 * @param {*} filterValue
 * @param {"text"|"number"|"date"} type
 */
function matchesSingle(rawCell, operator, filterValue, type) {
  const t = normalizeMultiDimensionType(type);
  const cellStr = String(rawCell ?? "");

  if (t === "text") {
    const fv = String(filterValue ?? "").trim();
    const lc = cellStr.toLowerCase();
    const lf = fv.toLowerCase();
    switch (operator) {
      case "contains":
        return lc.includes(lf);
      case "doesnotcontain":
        return !lc.includes(lf);
      case "eq":
        return cellStr === fv;
      case "neq":
        return cellStr !== fv;
      default:
        return true;
    }
  }

  if (t === "number") {
    const cellN = parseCellNumber(cellStr);
    const fv = Number(filterValue);
    if (Number.isNaN(fv)) return true;
    if (Number.isNaN(cellN)) return false;
    switch (operator) {
      case "eq":
        return cellN === fv;
      case "neq":
        return cellN !== fv;
      case "gt":
        return cellN > fv;
      case "gte":
        return cellN >= fv;
      case "lt":
        return cellN < fv;
      case "lte":
        return cellN <= fv;
      default:
        return true;
    }
  }

  if (t === "date") {
    const cellD = parseCellDate(cellStr);
    const filterD = startDayOrNull(filterValue);
    if (!filterD) return true;
    if (!cellD) return false;
    const ct = cellD.getTime();
    const ft = filterD.getTime();
    switch (operator) {
      case "eq":
        return ct === ft;
      case "neq":
        return ct !== ft;
      case "gt":
        return ct > ft;
      case "gte":
        return ct >= ft;
      case "lt":
        return ct < ft;
      case "lte":
        return ct <= ft;
      default:
        return true;
    }
  }

  return true;
}

/**
 * @param {string} rawCell
 * @param {{ logic: "and"|"or", filters: { operator: string, value: * }[] }} composite
 * @param {"text"|"number"|"date"} type
 */
export function cellMatchesCompositeFilter(rawCell, composite, type) {
  if (!composite?.filters?.length) return true;
  const t = normalizeMultiDimensionType(type);
  const parts = composite.filters.map((f) => {
    if (!hasActiveFilterValue(f.value, t)) return true;
    return matchesSingle(rawCell, f.operator, f.value, t);
  });
  return composite.logic === "or" ? parts.some(Boolean) : parts.every(Boolean);
}

/**
 * Frontend-only: keep rows that pass all per-column composite filters (dimensions + optional Options label filter).
 * @param {Array<{ id: string, label?: string, cells?: Record<string, string> }>} options
 * @param {Array<{ id: string, type?: string }>} dimensions
 * @param {Record<string, { logic: string, filters: { operator: string, value: * }[] }>} columnFilters
 * @param {"text"|"number"} [optionsLabelType] Type for filtering the Options/label column
 */
export function filterMultiDimensionOptions(options, dimensions, columnFilters, optionsLabelType = "text") {
  if (!columnFilters || !Object.keys(columnFilters).length) return options;
  const labelType = normalizeMultiDimensionType(optionsLabelType);
  return options.filter((opt) => {
    const passesAllDimensionFilters = dimensions.every((dim) => {
      const composite = columnFilters[dim.id];
      if (!composite) return true;
      const raw = opt.cells?.[dim.id] ?? "";
      return cellMatchesCompositeFilter(raw, composite, dim.type);
    });
    if (!passesAllDimensionFilters) return false;
    const labelComposite = columnFilters[MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID];
    if (!labelComposite) return true;
    const rawLabel = String(opt.label ?? "");
    return cellMatchesCompositeFilter(rawLabel, labelComposite, labelType);
  });
}
